import { TableCell, Tooltip, Stack, TableRow, Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { CopyToClipboardWrapper } from '@/components/buttons/copyToClipboardButton';
import { Icon } from '@/components/shared/icon';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { KmsSuggestedPolicyContent } from '@/components/vaults/kmsSuggestedPolicy';
import { useWorkspace } from '@/contexts/useWorkspace';

export const KmsKeyRow = ({
  kmsKeyId,
  isAwsManagedKey,
}: {
  kmsKeyId?: string;
  isAwsManagedKey?: boolean;
}) => {
  const { sharedDialog } = useWorkspace();

  if (!kmsKeyId) {
    return null;
  }

  const openSuggestPolicyDialog = () => {
    sharedDialog.open(
      'volumes-section-kms-suggested-policy',
      <KmsSuggestedPolicyContent
        kmsKey={kmsKeyId}
        cloudProvider={Provider.Aws}
        withRestoreAccounts={false}
      />,
      {
        isLoading: false,
        header: {
          title: 'Get suggested policy',
          subtitle:
            'Add this statement to your KMS key policy to grant access to your Eon scanning account. It allows for faster backups of your volumes and enables high-frequency backup policies.',
        },
      }
    );
  };

  return isAwsManagedKey ? (
    <TableRow>
      <TableCell className='props-col'>KMS key</TableCell>
      <TableCell>
        <Stack direction='row' alignItems='center' gap='4px'>
          <Typography>AWS-managed KMS key</Typography>
          <Tooltip
            title={`AWS-managed KMS keys can't be shared to other accounts, so Eon must first re-encrypt a snapshot of this volume before backing up. To enable faster backups and high-frequency backup policies, encrypt with a customer-managed KMS key instead.`}
            slotProps={{
              tooltip: {
                sx: {
                  padding: '16px',
                },
              },
            }}
          >
            <Icon iconClass='material-symbols-info-outline' />
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  ) : (
    <TableRow>
      <TableCell className='props-col'>KMS key ARN</TableCell>
      <TableCell>
        <Stack
          minWidth='0'
          direction='row'
          alignItems='center'
          gap='8px'
          justifyContent='space-between'
        >
          <CopyToClipboardWrapper textToCopy={kmsKeyId}>
            <OverflowTooltip text={kmsKeyId} />
          </CopyToClipboardWrapper>
          <ButtonWithIcon
            sx={{ flexShrink: 0 }}
            variant='text'
            text='Get Suggested Policy'
            icon='material-symbols-data-object'
            onClick={openSuggestPolicyDialog}
          />
        </Stack>
      </TableCell>
    </TableRow>
  );
};
